import { useState } from 'react';
import { createFileRoute, Link, useBlocker } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Chip, Panel, PanelHeader } from '@promanage/ui';
import {
  canRefundBond,
  canRefundRent,
  canViewTrustPosition,
  formatPeriod,
  nzDate,
  type PeriodStatus,
} from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { FinancialNav } from '../features/ledger/FinancialNav';
import { periodStatusLabel } from '../features/ledger/draft';
import { invalidateAccounts } from '../features/ledger/invalidate';
import { TrustPosition } from '../features/ledger/TrustPosition';
import { LockPeriodDialog } from '../features/ledger/LockPeriodDialog';
import { periodActions } from '../features/ledger/periodActions';
import { GenerateTransaction } from '../features/ledger/GenerateTransaction';
import { LeaveWarning } from '../features/ledger/LeaveWarning';
import { DraftQueue } from '../features/ledger/DraftQueue';
import {
  blankForm,
  pullBackIntoForm,
  typedLineCount,
  type GeneratorDraft,
  type GeneratorForm,
} from '../features/ledger/generatorDrafts';
import { trpc } from '../lib/trpc';
import { sessionQueryOptions, useSession } from '../features/auth/session';

export const Route = createFileRoute('/financial/')({
  loader: async ({ context }) => {
    // The root guard has already resolved the session. Periods and balances are a trust admin's
    // panels — see the note on them below — so nobody else pays for fetching them.
    const session = context.queryClient.getQueryData(sessionQueryOptions().queryKey);
    const mayViewPosition = canViewTrustPosition(session?.roles ?? []);
    const mayViewPeriods = mayViewPosition || (session?.mayClosePeriods ?? false);
    await Promise.all([
      mayViewPosition
        ? context.queryClient.prefetchQuery(
            trpc.ledger.position.queryOptions(undefined, { staleTime: 10_000 }),
          )
        : null,
      mayViewPeriods
        ? context.queryClient.prefetchQuery(
            trpc.ledger.periods.queryOptions(undefined, { staleTime: 10_000 }),
          )
        : null,
    ]);
  },
  component: LedgerPage,
});

/**
 * Financial — where the day's money is entered.
 *
 * WHAT THIS PAGE IS FOR CHANGED, and the change is the point. It used to be a balances panel
 * beside a long list of what had already happened, with the actual work — receipting money —
 * behind a modal on a button. That had it backwards: reading the ledger is occasional, and
 * receipting is the job an agency does every morning. The record moved to `/financial/transactions`
 * and its space here belongs to generating transactions and to the queue of drafts waiting to be
 * posted.
 *
 * BALANCES STAY, because they are the context the work is done against — an operator receipting
 * owner funds wants to see what that owner is holding while they do it. Periods stay because
 * locking a month is a decision about the ledger, not about a list.
 *
 * THE JOURNAL PATHS STAY TOO, as Journal entry in the Transactions top bar. The generator covers the money an agency actually
 * keys day to day — the receipts, and now the two bond movements that bracket a tenancy; a
 * correction, a reversal or anything the generator has no kind for still needs a hand-written
 * journal, and taking that away would leave the one job with no path at all.
 */
function LedgerPage() {
  const queryClient = useQueryClient();
  const [locking, setLocking] = useState<{ startsOn: string; status: PeriodStatus } | null>(null);

  const session = useSession();
  const roles = session?.roles ?? [];
  // The grant folded with the roles, server-side — `auth.me` says why it is one boolean.
  const mayLock = session?.mayClosePeriods ?? false;
  const mayViewPosition = canViewTrustPosition(roles);
  // Whoever may close a month has to be able to see the months — the grant can sit with a trust
  // role other than admin, and hiding the panel from them would leave the close with no path.
  const mayViewPeriods = mayViewPosition || mayLock;
  // Both are `trust_admin` today and are asked separately because core states them separately —
  // see `canRefundRent`. The generator offers the refund kinds only when BOTH are held, which is
  // the honest reading while they cannot diverge.
  const mayRefund = canRefundBond(roles) && canRefundRent(roles);

  /*
   * THE QUEUE LIVES IN THIS TAB, and nowhere else. A draft is a working note — it can be edited
   * or thrown away freely, which is exactly what a posted transaction can never be. Keeping it in
   * component state rather than on the server is what makes that true rather than merely stated.
   */
  const [drafts, setDrafts] = useState<GeneratorDraft[]>([]);

  /*
   * AND THE FORM ITSELF, held here rather than inside `GenerateTransaction`, because work now
   * moves BOTH WAYS between the panel and the queue: "Add to queue" sends lines down, and Edit on
   * a queued row sends one back up (`pullBackIntoForm`). A form owned by one of the two would mean
   * the other could only ask nicely.
   */
  const [form, setForm] = useState<GeneratorForm>(() =>
    blankForm(nzDate(new Date()), crypto.randomUUID()),
  );

  /*
   * The day a pulled-back draft was carrying, when the form was already busy with another one.
   * Kept here because the pull-back is decided here; shown by the panel, and it stops meaning
   * anything the moment the form's date matches it again.
   */
  const [droppedDate, setDroppedDate] = useState<string | null>(null);

  /**
   * Edit is a MOVE. The draft leaves the queue in the same update that puts it in the form, so it
   * exists in exactly one place at a time — never queued for posting while somebody is changing
   * it, and never lost between the two.
   */
  const editDraft = (draft: GeneratorDraft) => {
    const pulled = pullBackIntoForm(form, draft, crypto.randomUUID());
    setDrafts((current) => current.filter((d) => d.key !== draft.key));
    setForm(pulled.form);
    setDroppedDate(pulled.droppedDate);
  };

  /*
   * THE WARNING ON LEAVING, exactly as /financial/entry's queue carries one. Both halves matter and
   * cover different exits: `shouldBlockFn` catches navigation inside the app — clicking away to
   * Transactions or Reconcile with drafts still sitting here — and `enableBeforeUnload` catches
   * closing the tab, reloading, or the back button leaving the site. The queue is unsaved work by
   * design (see the note above), so this is the one place on this page worth interrupting for.
   */
  // A line half-typed in the generator is unsaved work too — and since Edit moves a draft out of
  // the queue and into that form, a warning that only counted the queue would fall silent at
  // exactly the moment somebody was mid-correction.
  const unsaved = drafts.length + typedLineCount(form);
  const dirty = unsaved > 0;
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  const position = useQuery(
    trpc.ledger.position.queryOptions(undefined, { staleTime: 10_000, enabled: mayViewPosition }),
  );
  const periods = useQuery(
    trpc.ledger.periods.queryOptions(undefined, { staleTime: 10_000, enabled: mayViewPeriods }),
  );
  const actions = periodActions(periods.data ?? [], nzDate(new Date()));

  // Locking a period changes no balance, but it changes what the periods panel says and whether
  // the next transaction is a late item — so all three go.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.ledger.periods.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.ledger.transactions.queryKey() });
    invalidateAccounts(queryClient);
  };

  const onProcessed = (postedKeys: string[], refusals: Record<string, string>) => {
    /*
     * The posted ones leave and the refused ones stay, wearing their reason. A queue that emptied
     * whatever happened would lose the drafts nobody has dealt with; one that kept everything
     * would offer to post the same receipt twice.
     */
    setDrafts((current) =>
      current
        .filter((draft) => !postedKeys.includes(draft.key))
        .map((draft) => ({ ...draft, refusal: refusals[draft.key] ?? draft.refusal })),
    );

    refresh();
    // A receipt settles rent charges, so every arrears figure on every screen has moved.
    void queryClient.invalidateQueries({ queryKey: trpc.rent.position.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.rent.charges.queryKey() });
    /*
     * And every bond position, without naming a tenancy. A run can carry a bond receipt and a
     * refund against different tenancies, and the held balance, the status and the lodgement
     * estimate the Bond panel shows are all computed on read — so what has to be thrown away is
     * whichever of them this browser is still holding, not one known key.
     */
    void queryClient.invalidateQueries({ queryKey: trpc.bond.position.queryKey() });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Financial"
        subtitle={
          position.data
            ? `${String(position.data.groups.reduce((total, group) => total + group.accountCount, 0))} accounts`
            : mayViewPosition
              ? 'Balances, periods and the day’s entries'
              : 'The day’s entries'
        }
      />
      {/*
       * Transactions, Commission and Disbursements are tabs across the section rather than
       * buttons in this page's top bar — see `SectionNav`. Journal entry is a button on the
       * Transactions top bar. Tenancy invoices moved to Bills, beside the supplier bills they
       * recover.
       */}
      <FinancialNav />

      <div
        className={
          mayViewPeriods ? 'grid gap-4 lg:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]' : 'grid gap-4'
        }
      >
        {/*
         * PERIODS AND BALANCES ARE A TRUST ADMIN'S, for the reason in `canViewTrustPosition` — a
         * presentation rule, so it withholds nothing the server would not. Periods also show for
         * whoever holds the close grant. Everyone else gets the day's work, full width.
         */}
        {mayViewPeriods ? (
          <div className="flex min-w-0 flex-col gap-4">
            {/* ── Periods ──────────────────────────────────────────────────── */}
            <Panel className="p-0">
              <PanelHeader
                title="Periods"
                subtitle="Months close oldest first, and reopen newest first"
                className="px-4 pt-4 pb-2"
              />

              {periods.data && periods.data.length > 0 ? (
                <ul className="flex flex-col">
                  {periods.data.map((row) => (
                    <li key={row.id} className="border-t border-rule">
                      <div className="flex items-center gap-2 px-4 py-2.5">
                        {/*
                         * A LINK now, not a filter on this page. The list it filtered lives at
                         * /financial/transactions, and a month somebody wants to read is a month they
                         * want to send to a colleague — which a search param survives and component
                         * state never did.
                         */}
                        <Link
                          to="/financial/transactions"
                          search={{ period: row.startsOn }}
                          className="min-w-0 flex-1 text-left text-[0.8125rem] font-medium hover:underline"
                        >
                          {formatPeriod(row.startsOn)}
                          {/*
                           * Journals, not transactions. This is the accountant's count — what a
                           * month contains when it is signed off, and what the lock dialog names.
                           */}
                          <span className="ml-1.5 text-[0.6875rem] font-normal text-ink-subtle">
                            {row.journalCount} {row.journalCount === 1 ? 'journal' : 'journals'}
                          </span>
                        </Link>

                        {/*
                         * A locked month is `neutral`, not a warning. Signing a month off is the
                         * desired end state, and colouring it like a problem would teach people to
                         * avoid doing the thing they are supposed to do.
                         */}
                        <Chip tone={row.status === 'locked' ? 'neutral' : 'accent'}>
                          {periodStatusLabel(row.status)}
                        </Chip>

                        {/*
                         * Only for whoever can actually go through with it, and only on the one
                         * month that could be next — see `periodActions`. A button anyone else
                         * presses is a control that exists only to say no, and on the reopen path
                         * it reads as an invitation to undo a closed month.
                         */}
                        {mayLock && actions.has(row.startsOn) ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              setLocking({ startsOn: row.startsOn, status: row.status })
                            }
                          >
                            {actions.get(row.startsOn) === 'reopen' ? 'Reopen' : 'Close'}
                          </Button>
                        ) : null}
                      </div>

                      {row.unlockReason ? (
                        // In front of people, not only in the audit log: a reopened month is a
                        // month whose signed-off figures were allowed to move.
                        <p className="px-4 pb-2.5 text-[0.6875rem] text-ink-muted">
                          Reopened — {row.unlockReason}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-4 pb-4 text-[0.8125rem] text-ink-muted">
                  Periods open themselves when the first journal posts into a month.
                </p>
              )}
            </Panel>

            {/* ── Balances ─────────────────────────────────────────────────── */}
            {mayViewPosition ? <TrustPosition /> : null}
          </div>
        ) : null}

        {/* ── The day's work ─────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-4">
          <GenerateTransaction
            form={form}
            onFormChange={setForm}
            droppedDate={droppedDate}
            mayRefund={mayRefund}
            // One call per submit, carrying one draft per line the form held — the form has
            // already split them; this just appends the lot to the queue. Whatever the form was
            // told about a dropped date went with them, so it stops being said.
            onQueue={(newDrafts) => {
              setDrafts((current) => [...current, ...newDrafts]);
              setDroppedDate(null);
            }}
          />

          <DraftQueue
            drafts={drafts}
            onEdit={editDraft}
            onRemove={(key) => setDrafts((current) => current.filter((d) => d.key !== key))}
            onProcessed={onProcessed}
          />
        </div>
      </div>

      {locking ? (
        <LockPeriodDialog
          startsOn={locking.startsOn}
          status={locking.status}
          onClose={() => setLocking(null)}
          onDone={() => {
            setLocking(null);
            refresh();
          }}
        />
      ) : null}

      {blocker.status === 'blocked' ? (
        <LeaveWarning count={unsaved} onStay={blocker.reset} onLeave={blocker.proceed} />
      ) : null}
    </div>
  );
}
